/**
 * Phase 18b — one list's overrides: its name and what it overrides in words
 * on the first line; the amount, gateway pin and three send options under
 * it, each with a "container default" choice. The pin picks a LANE of the
 * pool (Phase 40: "B · 23Telecom · AUSSIEWIN"); a pin saved before lanes
 * shows its route's first lane. A slot's own amount or gateway pin still
 * wins — the hint says so once, above the cards.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { X } from 'lucide-react';
import { describeListOverride, type DropGenListOverride, type DropGenListOverrideOptions } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import type { LaneOption } from '../../../../utils/lanes/lane-words';

const FIELD = 'flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';
const CONTROL = 'h-7 rounded-md border border-border/60 bg-background px-2 text-xs normal-case tracking-normal outline-none focus:border-primary';
const SEG = (on: boolean) => cn('rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider', on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground');
/** A pinned route that is not in the pool any more keeps its pin, named. */
const OUTSIDE = 'route:';

export type LanePinPatch = Partial<Pick<DropGenListOverride, 'amount' | 'routeId' | 'gatewayRowId'>>;

interface Props {
  override: DropGenListOverride;
  listName: string;
  /** The pool's lanes, lettered. */
  lanes: LaneOption[];
  routeName: (routeId: number) => string | null;
  tlds: Array<{ value: string; label: string }>;
  onPatch: (patch: LanePinPatch) => void;
  onOption: <K extends keyof DropGenListOverrideOptions>(key: K, value: DropGenListOverrideOptions[K] | undefined) => void;
  onRemove: () => void;
}

const TriState: FC<{ label: string; value: boolean | undefined; onChange: (v: boolean | undefined) => void }> = ({ label, value, onChange }) => (
  <span className={FIELD}>
    {label}
    <span className="flex items-center gap-0.5 rounded-md border border-border/60 bg-muted/30 p-0.5">
      <button type="button" className={SEG(value === undefined)} onClick={() => onChange(undefined)}>Default</button>
      <button type="button" className={SEG(value === true)} onClick={() => onChange(true)}>On</button>
      <button type="button" className={SEG(value === false)} onClick={() => onChange(false)}>Off</button>
    </span>
  </span>
);

/** The lane the stored pin points at: its own lane on that route, else the route's first lane, else "outside the pool". */
function pinnedValue(o: DropGenListOverride, lanes: LaneOption[]): string {
  if (o.routeId === null) return '';
  const own = lanes.find((l) => l.value === o.gatewayRowId && l.routeId === o.routeId) ?? lanes.find((l) => l.routeId === o.routeId);
  return own ? own.value : `${OUTSIDE}${o.routeId}`;
}

export const ListOverrideCard: FC<Props> = ({ override: o, listName, lanes, routeName, tlds, onPatch, onOption, onRemove }) => {
  const value = pinnedValue(o, lanes);
  const pinned = lanes.find((l) => l.value === value);
  const words = pinned ? pinned.label : o.routeId !== null ? routeName(o.routeId) : null;
  const pick = (v: string) => {
    if (v === '') return onPatch({ routeId: null, gatewayRowId: null });
    const lane = lanes.find((l) => l.value === v);
    if (lane) onPatch({ routeId: lane.routeId, gatewayRowId: lane.value });
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border/60 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="min-w-0 truncate text-sm font-medium">{listName}</span>
        <span className="truncate text-xs text-muted-foreground">{describeListOverride(o, words)}</span>
        <span className="grow" />
        <button type="button" aria-label={`Remove the overrides of ${listName}`} onClick={onRemove} className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label className={FIELD}>
          Amount
          <input type="number" min={1} value={o.amount ?? ''} placeholder="default" onChange={(e) => onPatch({ amount: e.target.value === '' ? null : Math.max(1, Math.floor(Number(e.target.value))) })} className={cn(CONTROL, 'w-24 tabular-nums')} />
        </label>
        <label className={FIELD}>
          Gateway
          <select value={value} onChange={(e) => pick(e.target.value)} className={cn(CONTROL, 'max-w-[16rem]')}>
            <option value="">The strategy decides</option>
            {lanes.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
            {value.startsWith(OUTSIDE) && o.routeId !== null ? <option value={value}>{routeName(o.routeId) ?? `route ${o.routeId}`} — not in the pool</option> : null}
          </select>
        </label>
        <label className={FIELD}>
          Link domain
          <select value={o.options?.shortLinkTldValue ?? ''} onChange={(e) => onOption('shortLinkTldValue', e.target.value === '' ? undefined : e.target.value)} className={cn(CONTROL, 'max-w-[12rem]')}>
            <option value="">Container default</option>
            {tlds.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <TriState label="Duplicates" value={o.options?.forceSend} onChange={(v) => onOption('forceSend', v)} />
        <TriState label="Only new domains" value={o.options?.useOnlyNewDomains} onChange={(v) => onOption('useOnlyNewDomains', v)} />
      </div>
    </div>
  );
};
